import { useCallback, useContext, useRef, useState } from "react";

// hooks
import { captureError, useEffectOnce, useMountedRef } from "@swiggy-private/core-ui";

// context
import {
    ApiContext,
    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS,
    GlobalHeaderState,
    isEmpty,
    NEWRELIC_TABLES,
    PublicApiContext,
    recordNREvent,
    SwgyHeaderKeysList,
} from "@swiggy-private/checkout-core";

// helpers
import { isBusinessLinePayOnDelivery, isBusinessLineDash } from "@payment-client/helpers/Misc";
import { RequestCartKeyForPayOnDeliverySuccessPayload } from "@payment-client/interfaces/Checkout";
import { confirmOrder } from "@payment-client/apis/Order";
import { confirmOrderPublic } from "@payment-client/apis/publicOrder";

// constants
import { API_SUCCESS_STATUS_CODE, POD_ORDER_ALREADY_PAID } from "@payment-client/constants/StatusCodes";
import {
    OOPS_SOMETHING_WENT_WRONG,
    PAY_ON_DELIVERY_PREVIOUS_PAYMENT_SUCCESSFUL,
} from "@payment-client/constants/TextDict";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";
import { registerPayOnDeliveryPublic } from "@payment-client/apis/publicOrder";
import { generateCartKeyForPayOnDelivery } from "@payment-client/apis/Payment";
import { PAYMENT_CONFIG } from "@payment-client/constants/Hosts";

async function fetchCartKeyFromOrderIdPayOnDelivery(apiHost: string, orderId: string): Promise<string | unknown> {
    try {
        const payonDeliveryFn =
            PAYMENT_CONFIG.REGISTER_PAY_ON_DELIVERY_PUBLIC === "true"
                ? registerPayOnDeliveryPublic
                : generateCartKeyForPayOnDelivery;
        const response = await payonDeliveryFn(apiHost, { orderId });
        return (response.data as RequestCartKeyForPayOnDeliverySuccessPayload)?.cart_key;
    } catch (e) {
        return Promise.reject(e);
    }
}

type UseCartKeyReturnType = {
    cartKey?: string;
    error?: {
        statusCode?: number;
        data: {
            title?: string;
            subTitle?: string;
        };
        [key: string]: unknown;
    };
};

const useCartKey = (): UseCartKeyReturnType => {
    const apiContextValue = useContext(ApiContext);
    const apiHost = apiContextValue.host.unwrap();
    const headerCartKey = GlobalHeaderState.getHeader(SwgyHeaderKeysList.cartkey);

    const publicApiConfig = useContext(PublicApiContext);

    const [data, setData] = useState<UseCartKeyReturnType>(() => ({
        cartKey: headerCartKey,
        error: undefined,
    }));

    const fetchCartKeyApiCallRef = useRef(false);
    const isMountedRef = useMountedRef();

    const orderIdFromHeader = GlobalHeaderState.getHeader(SwgyHeaderKeysList.orderid);
    const transactionIdFromHeader = GlobalHeaderState.getHeader(SwgyHeaderKeysList.transactionid);
    const isPayOnDeliveryFlow = isBusinessLinePayOnDelivery();

    const isConfirmOrderEnabled = isPublicApiEnabled(
        publicApiConfig || {},
        CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.ORDER_APIS,
        CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.CONFIRM_ORDER,
    );

    const fetchCartKeyForPayOnDelivery = useCallback(() => {
        if (fetchCartKeyApiCallRef.current) {
            return;
        }

        fetchCartKeyApiCallRef.current = true;

        fetchCartKeyFromOrderIdPayOnDelivery(apiHost, orderIdFromHeader)
            .then(cartKeyResponse => {
                fetchCartKeyApiCallRef.current = false;

                GlobalHeaderState.setHeader(SwgyHeaderKeysList.cartkey, cartKeyResponse as string);
                setData({ cartKey: cartKeyResponse as string, error: undefined });
            })
            .catch(err => {
                fetchCartKeyApiCallRef.current = false;

                if (isMountedRef.current) {
                    setData({ cartKey: undefined, error: err });
                }
            });
    }, [apiHost, isMountedRef, orderIdFromHeader]);

    // for cases where we get transaction_id in headers we will
    // 1. cancel the pending transaction using order id and transaction id
    // 2. then initiate cart key api call
    const closePreviousPendingTransaction = useCallback(() => {
        if (isEmpty(orderIdFromHeader) || isEmpty(transactionIdFromHeader)) {
            return;
        }

        const confirmOrderFn = isConfirmOrderEnabled ? confirmOrderPublic : confirmOrder;

        confirmOrderFn(apiHost, { orderId: orderIdFromHeader, transactionId: transactionIdFromHeader })
            .then(response => {
                // iff confirm order is successful it means last transaction completed
                if (response.statusCode === API_SUCCESS_STATUS_CODE) {
                    setData({
                        cartKey: undefined,
                        error: {
                            statusCode: POD_ORDER_ALREADY_PAID,
                            data: { title: PAY_ON_DELIVERY_PREVIOUS_PAYMENT_SUCCESSFUL, subTitle: "" },
                        },
                    });
                } else {
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.PAYMENT_AUTOPAY_CANCEL_TRANSACTION_FAILED,
                        orderId: orderIdFromHeader,
                        transactionId: transactionIdFromHeader,
                        statusCode: response.statusCode ?? "",
                        statusMessage: response.statusMessage ?? "",
                    });
                    if (isEmpty(data.cartKey)) {
                        fetchCartKeyForPayOnDelivery();
                        return;
                    }
                }
            })
            .catch(err => {
                if (isMountedRef.current) {
                    setData({
                        cartKey: undefined,
                        error: { data: { title: OOPS_SOMETHING_WENT_WRONG, subTitle: "" } },
                    });
                }

                captureError(err);
            });
    }, [
        apiHost,
        data.cartKey,
        fetchCartKeyForPayOnDelivery,
        isMountedRef,
        orderIdFromHeader,
        transactionIdFromHeader,
        isConfirmOrderEnabled,
    ]);

    useEffectOnce(() => {
        if (!isPayOnDeliveryFlow) {
            return;
        }

        if (!isEmpty(transactionIdFromHeader)) {
            closePreviousPendingTransaction();
            return;
        }

        if (isEmpty(data.cartKey)) {
            fetchCartKeyForPayOnDelivery();
            return;
        }
    });

    /**
     *  To get an always updated cartKey for business line dash
     *
     */
    if (isBusinessLineDash() && headerCartKey !== data.cartKey) {
        return {
            cartKey: headerCartKey,
            error: undefined,
        };
    }

    return data;
};

export default useCartKey;
